import {
  RPC_ERROR_AUTHENTICATION_FAILED,
  RPC_ERROR_REQUESTER_VALIDATION_FAILED
} from '../core/constants';
import { JSONRPCError } from '../core/errors';

/**
 * Walk an error's `error`/`cause` chain looking for a {@link JSONRPCError}.
 * Errors thrown by call creation are wrapped (e.g. `CallCreateError`), so the
 * underlying signaling error is nested. Bounded by a visited set to guard
 * against cyclic causes.
 */
export function findJSONRPCError(error: unknown): JSONRPCError | undefined {
  const seen = new Set<unknown>();
  let current: unknown = error;
  while (current instanceof Error && !seen.has(current)) {
    seen.add(current);
    if (current instanceof JSONRPCError) {
      return current;
    }
    current = (current as { error?: unknown }).error ?? current.cause;
  }
  return undefined;
}

/**
 * Whether an error is a session-recoverable authentication failure
 * (`-32002` authentication failed or `-32003` requester validation failed)
 * that a credential re-mint + retry can heal.
 */
export function isRecoverableAuthError(error: unknown): boolean {
  const rpcError = findJSONRPCError(error);
  return (
    rpcError !== undefined &&
    (rpcError.code === RPC_ERROR_REQUESTER_VALIDATION_FAILED ||
      rpcError.code === RPC_ERROR_AUTHENTICATION_FAILED)
  );
}
